import { Component } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ROLES } from 'src/app/authorization/registration/registration.component';
import { User } from 'src/app/core/objects/user';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { UserService } from 'src/app/core/services/user.service';
import { validateCheckboxRequired } from 'src/app/core/validators/validators';
import { ModalOverlayRef } from 'src/app/modal/modal-overlay-ref';
import { isMatchingControl } from 'src/app/shared/validators/validators';

@Component({
    selector: 'gl-roles-email-popup',
    templateUrl: './roles-email-popup.component.html',
    styleUrls: ['./roles-email-popup.component.scss'],
    standalone: false
})
export class RolesEmailPopupComponent {
  public form: CustomForm;
  public user: User;
  public roles: string[] = ROLES;
  public acceptedTermsOfServiceAndPrivacyPolicy: string;
  public privacyPolicyLink: string;
  public regulationsLink: string;

  constructor(
    private userService: UserService,
    private modalRef: ModalOverlayRef,
    private userApiService: UserApiService,
    private translate: TranslateService,
  ) {
    this.userService.user.subscribe(user => {
      this.user = user;
      this.acceptedTermsOfServiceAndPrivacyPolicy = this.user.acceptedConsents?.find(
        value => value === 'termsOfServiceAndPrivacyPolicy',
      );
    });

    this.privacyPolicyLink = `https://www.goodloading.com/${this.translate.currentLang}/privacy-policy/`;
    this.regulationsLink = `https://www.goodloading.com/${this.translate.currentLang}/regulations/`;

    this.form = this.generateForm();
  }

  public close(whatChange?: string) {
    this.modalRef.close(whatChange);
  }

  public goTo(event: MouseEvent, link: string) {
    event.stopPropagation();
    event.preventDefault();
    window.open(link, '_blank');
  }

  public onSubmit() {
    if (this.form.valid) {
      const dateUpdate = {
        lang: this.translate.currentLang,
        email: this.form.value.email ? this.form.value.email : undefined,
        role: this.form.value.role ? this.form.value.role : undefined,
        newPassword: this.form.value.password ? this.form.value.password : undefined,
        repeatedNewPassword: this.form.value.retypedPassword
          ? this.form.value.retypedPassword
          : undefined,
        consents: this.form.value.acceptedTermsOfServiceAndPrivacyPolicy
          ? [
              {
                type: 'termsOfServiceAndPrivacyPolicy',
                isAccepted: this.form.value.acceptedTermsOfServiceAndPrivacyPolicy,
              },
            ]
          : undefined,
      };
      this.userApiService
        .updateUserInfo(dateUpdate)
        .subscribe(() => (dateUpdate.newPassword ? this.close('newPassword') : this.close()));
    }
  }

  public checkIsHaveEmailProvider() {
    return !!this.user.providers?.find(el => el === 'email');
  }
  private isPasswordValid(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid: boolean = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(control.value);
      return isValid ? null : { wrongPassword: true };
    };
  }
  private generateForm(): CustomForm {
    const form = new UntypedFormGroup({});
    if (!this.user.email) {
      form.addControl('email', new UntypedFormControl(null, [Validators.email, Validators.required]));
    }
    if (!this.user.role) {
      form.addControl('role', new UntypedFormControl(this.user.role, [Validators.required]));
    }
    if (!this.checkIsHaveEmailProvider()) {
      const password = new UntypedFormControl(null, [this.isPasswordValid(), Validators.required]);
      const repeatedPassword = new UntypedFormControl(null, [
        Validators.required,
        isMatchingControl(password),
      ]);
      form.addControl('password', password);
      form.addControl('retypedPassword', repeatedPassword);
    }

    if (!this.acceptedTermsOfServiceAndPrivacyPolicy) {
      form.addControl(
        'acceptedTermsOfServiceAndPrivacyPolicy',
        new UntypedFormControl(false, [validateCheckboxRequired]),
      );
    }
    return form;
  }
}

interface CustomForm extends UntypedFormGroup {
  value: {
    email?: string;
    role?: string;
    password?: string;
    retypedPassword: string;
    acceptedTermsOfServiceAndPrivacyPolicy: string;
  };
}
